import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { filter } from 'lodash'
import * as React from 'react'
import { ReplyContentAudioDTO, ReplyContentAudioVoiceDTO, ReplyDTO } from '../../../../api/gen/base'
import { ReplyContent } from '../../../../api/gen/base/types/ReplyContent'
import CalendarArrowUpIcon from '../../../../assets/icons/CalendarArrowUp.svg?react'
import DownloadIcon from '../../../../assets/icons/DownloadIcon.svg?react'
import PenSquare from '../../../../assets/icons/PenSquare.svg?react'
import RefreshIcon from '../../../../assets/icons/Refresh.svg?react'
import { CopyText } from '../../../../components/CopyText'
import { Notistack } from '../../../../components/Notistack'
import { REGULAR_REQUESTS_LIMIT_FOR_THREAD } from '../../../../constants/regularRequests.constants'
import {
    RegularRequestCreateDialog,
    RegularRequestCreateDialogData,
} from '../../../../dialogs/RegularRequestCreateDialog'
import { ReplyDeleteDialog } from '../../../../dialogs/ReplyDeleteDialog'
import { useRequestsStore } from '../../../../store/storeRequests'
import { useWrapperStore } from '../../../../store/storeWrapper'
import {
    getRegularRequestFormFilesFromReplyContent,
    getTextContentFromReplyContent,
} from '../../../../utils/regularRequests'
import { ReplyStatus } from '../../../../swagger/dto'

type Props = {
    threadId: number
    request: ReplyDTO
    requestContent: ReplyContent[]
    totalScheduledReplyCount: number
    isActorUser: boolean
}

export const ReplyMenu: React.FC<Props> = ({
    threadId,
    request,
    requestContent,
    totalScheduledReplyCount,
    isActorUser,
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const [openRemoveDialog, setOpenRemoveDialog] = React.useState<boolean>(false)
    const [hardDelete, setHardDelete] = React.useState<boolean>(false)
    const [regularRequestDialogData, setRegularRequestDialogData] =
        React.useState<RegularRequestCreateDialogData | null>(null)
    const [createRegularRequestError, setCreateRegularRequestError] = React.useState(false)

    const {
        updateRegularRequestsCountInRequestDetail,
        removeReturnReply,
        regenerateRequestMessage,
        requestMessages
    } = useRequestsStore((state) => state)

    const { isTempUser, modelDetail, setRequestByEdit } = useWrapperStore((state) => state)

    const open = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setOpenRemoveDialog(false)
        setAnchorEl(null)
    }

    const handleSuccessCreateRegularRequest = () => {
        updateRegularRequestsCountInRequestDetail({ threadId })
    }

    const handleClickOpenRemoveDialog = () => {
        setHardDelete(false)
        setOpenRemoveDialog(true)
    }

    const handleClickOpenHardRemoveDialog = () => {
        setHardDelete(true)
        setOpenRemoveDialog(true)
    }

    const handleOpenRegularRequestCreateDialog = () => {
        if (
            totalScheduledReplyCount &&
            totalScheduledReplyCount >= REGULAR_REQUESTS_LIMIT_FOR_THREAD
        ) {
            setCreateRegularRequestError(true)
        } else {
            const text = getTextContentFromReplyContent(requestContent)

            setRegularRequestDialogData({
                text: text?.text ?? '',
                files: getRegularRequestFormFilesFromReplyContent(requestContent),
                threadId,
            })
        }

        setAnchorEl(null)
    }

    const handleClickEditRequest = () => {
        handleClose()

        const contentText = filter(request.content, {
            content_type: 'TEXT',
        })
        if (contentText && contentText.length) {
            setRequestByEdit(request)
        }
    }

    const handleCloseRegularRequestCreateDialog = () => {
        setRegularRequestDialogData(null)
    }

    const handleClickRemoveReturnReply = async (id) => {
        setAnchorEl(null)
        await removeReturnReply(id)
    }

    const handleClickGenerateNewVariant = async () => {
        handleClose()

        await regenerateRequestMessage(
            {
                thread_id: threadId,
                regenerate: true,
                parental_reply_id: request.id,
                contexts: [],
            },
            modelDetail?.is_free ? 0 : 1,

        )
    }

    const linkedReplyIds = request.linked_replies ? request.linked_replies.map((item) =>
        item.id ?? 0,
    ) : []
    const replyId = request.id
    const replyIds = [request.id, ...linkedReplyIds]
    const isAudioRequest = requestContent.some(({ content_type }) => content_type === 'AUDIO_VOICE')
    const audioToDownload = requestContent.find(
        ({ content_type }) => content_type === 'AUDIO' || content_type === 'AUDIO_VOICE',
    ) as (ReplyContentAudioDTO & ReplyContentAudioVoiceDTO) | undefined

    const contentByText = getTextContentFromReplyContent(requestContent)

    const handleDownloadFile = (filePath: string, name: string) => {
        fetch(filePath)
            .then((response) => response.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                const fileExtension = filePath.slice(filePath.lastIndexOf('.') + 1)
                const datetime = request.finished_at ?? request.created_at
                link.href = url
                link.download = `${dayjs(datetime).format('DD.MM.YYYY_HH-mm')}_${name}.${fileExtension}`
                link.click()
                link.remove()
            })
        handleClose()
    }

    const isHasEdit = () => {
        const targetIndex = requestMessages.findIndex(item => item.id === replyId);

        if (targetIndex === -1) return null;

        const previousAssistantIndex = requestMessages
            .slice(0, targetIndex)
            .findLastIndex(item => item.actor === "ASSISTANT");

        if (previousAssistantIndex === -1) return null;

        return requestMessages[previousAssistantIndex].is_regeneration_available;
    };

    return (
        <>
            <IconButton
                sx={{
                    '&:hover': { borderColor: '#F4F4F6' },
                    background: ' #fff',
                    width: '24px',
                    height: '24px',
                    padding: '4px',
                }}
                variant="text"
                color="primary"
                aria-expanded={open ? 'true' : undefined}
                onClick={(e) => handleClick(e)}>
                <MoreVertOutlinedIcon
                    sx={{
                        width: '16px',
                        height: '16px',
                    }}
                />
            </IconButton>
            {replyId && (
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}>
                    {false && isActorUser && !isTempUser && !request?.is_deleted && isHasEdit() && (
                        <MenuItem onClick={handleClickEditRequest}>
                            <ListItemText
                                primary={
                                    <Typography variant="body" color="#181B25">
                                        Редактировать запрос
                                    </Typography>
                                }
                            />
                            <PenSquare color="#181B25" />
                        </MenuItem>
                    )}
                    {isActorUser && !isAudioRequest && !isTempUser && !request?.is_deleted && (
                        <MenuItem onClick={handleOpenRegularRequestCreateDialog}>
                            <ListItemText
                                primary={
                                    <Typography variant="body" color="#181B25">
                                        Добавить в регулярные запросы
                                    </Typography>
                                }
                            />
                            <CalendarArrowUpIcon color="#181B25" />
                        </MenuItem>
                    )}
                    {!isActorUser &&
                        !isTempUser &&
                        request?.is_regeneration_available &&
                        !request?.is_deleted && (
                            <MenuItem onClick={handleClickGenerateNewVariant}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body" color="#5A5F72">
                                            Перегенерировать
                                        </Typography>
                                    }
                                />
                                <RefreshIcon color="#5A5F72" />
                            </MenuItem>
                        )}
                    {audioToDownload && (
                        <MenuItem
                            onClick={() =>
                                handleDownloadFile(
                                    audioToDownload.audio_url ?? audioToDownload.voice_url ?? '',
                                    'аудиозапись',
                                )
                            }>
                            <ListItemText
                                primary={
                                    <Typography variant="body" color="#5A5F72">
                                        Скачать аудио
                                    </Typography>
                                }
                            />
                            <DownloadIcon width={20} height={20} color="#5A5F72" />
                        </MenuItem>
                    )}
                    {contentByText?.text && <CopyText text={contentByText.text} />}
                    {!request?.is_deleted && request?.status === ReplyStatus.FINISHED && (
                        <>
                            <MenuItem onClick={handleClickOpenRemoveDialog}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body" style={{ color: '#CA1616' }}>
                                            Убрать из контекста
                                        </Typography>
                                    }
                                />
                                <DeleteOutlinedIcon fontSize="small" style={{ color: '#CA1616' }} />
                            </MenuItem>
                            <MenuItem onClick={handleClickOpenHardRemoveDialog}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body" style={{ color: '#CA1616' }}>
                                            Удалить
                                        </Typography>
                                    }
                                />
                                <DeleteForeverOutlinedIcon fontSize="small" style={{ color: '#CA1616' }} />
                            </MenuItem>
                        </>
                    )}
                    {request?.is_deleted && request?.status === ReplyStatus.FINISHED && (
                        <>
                            <MenuItem onClick={() => handleClickRemoveReturnReply(replyId)}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body">Восстановить в контекст</Typography>
                                    }
                                />
                                <RestoreFromTrashOutlinedIcon fontSize="small" />
                            </MenuItem>
                            <MenuItem onClick={handleClickOpenHardRemoveDialog}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body" style={{ color: '#CA1616' }}>
                                            Удалить
                                        </Typography>
                                    }
                                />
                                <DeleteForeverOutlinedIcon fontSize="small" style={{ color: '#CA1616' }} />
                            </MenuItem>
                        </>
                    )}
                </Menu>
            )}

            {openRemoveDialog && replyIds && (
                <ReplyDeleteDialog open={openRemoveDialog} handleClose={handleClose} ids={replyIds} hardDelete={hardDelete} />
            )}

            <RegularRequestCreateDialog
                open={!!regularRequestDialogData}
                initialData={regularRequestDialogData}
                onClose={handleCloseRegularRequestCreateDialog}
                onSubmit={handleSuccessCreateRegularRequest}
            />
            <Notistack
                isError
                open={createRegularRequestError}
                message="Превышен лимит запросов по расписанию для одного диалога"
                onClose={() => setCreateRegularRequestError(false)}
            />
        </>
    )
}
